// ═══════════════════════════════════════════════════════════════════════════
// Landing de produção `/` — Blink Telemedicina.
//
// REDESIGN 14/10/2026 (2ª) — pedido do dono: "elementos mais bem pensados, tipo
// a do /emagrecimento", com visuais, animações e motion, MUITO otimizada para
// celular e para carregar rápido ("se ficar carregando, os pacientes desistem").
//
// ARQUITETURA DE CARREGAMENTO (decisões de performance — não desfazer):
//  - Esta página é importada de forma EAGER pelo router (vai no bundle
//    principal). Por isso aqui fica SÓ o que aparece acima da dobra: cabeçalho,
//    hero com o celular, faixa rolando e a barra do atendente.
//  - Tudo abaixo da dobra mora em `sections.tsx`, carregado sob demanda (chunk
//    separado), com o download disparado logo após a primeira pintura — quando
//    a pessoa rola, já chegou; e o bundle principal quase não cresce.
//  - O hero NÃO depende de JS de rolagem para ficar visível: a entrada é
//    animação CSS pura (`lt-enter`), e o título só desliza (não começa
//    transparente), para não atrasar o maior conteúdo da tela (LCP).
//  - Ícones do hero são SVG inline (`icons.tsx`): a fonte Remix carrega
//    assíncrona e não pode deixar buraco no primeiro quadro.
//  - Animações contínuas só com transform/opacity e PAUSADAS fora da tela
//    (classe `lt-off` + `lt-loop`); o "vídeo" do celular só roda os timers
//    enquanto visível. `prefers-reduced-motion` desliga tudo.
//  - Zero vídeo/GIF/imagem raster: tudo CSS/SVG.
//
// DIREÇÃO VISUAL: CLARA (saúde = clareza e pureza). O celular escuro com cards
// brancos flutuando vem do /emagrecimento; contraste em verde-profundo só
// pontualmente (seção de confiança).
//
// REGRA EDITORIAL (não remover): só o que a Blink entrega de fato — R$ 48,90
// (preço normal, SEM "de/por", riscado ou promoção — pedido do dono 14/10/2026), pagamento único, chat OU vídeo, receita ICP-Brasil (Memed),
// receituários azul (B) e amarela (A) com médicos habilitados quando houver
// indicação clínica, atestado, pedido de exames, CRM conferido no CFM, sigilo e
// LGPD, sem aplicativo, todo o Brasil, PIX ou cartão (Pagar.me), plantão ou
// agendada. NENHUM número inventado (pacientes, %, tempo de espera, nota) e
// NENHUM depoimento fictício. Mockups levam o rótulo "Simulação ilustrativa".
// ═══════════════════════════════════════════════════════════════════════════
import { lazy, Suspense, useEffect, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import SeoHead from "@/components/base/SeoHead";
import { useInView, prefersReducedMotion } from "@/pages/emagrecimento/landingKit";
import FloatingWhatsApp from "@/pages/home/components/FloatingWhatsApp";
import { trackEvent } from "@/lib/analytics";
import { getSessionId } from "@/lib/session";
import { destinoLogado } from "@/hooks/useRedirectIfLoggedIn";
import { lazyRetry } from "@/lib/lazyRetry";
import { Bolt, HangUp, Ico, type IconName } from "./icons";
import { DUVIDAS, PRECO } from "./data";
import "./landing.css";

/* Abaixo da dobra: chunk próprio. O import é disparado no mount (ver efeito na
   página), então costuma já estar pronto quando a pessoa rola. */
const importarSecoes = () => lazyRetry(() => import("./sections"));
const Secoes = lazy(importarSecoes);

/* ── SEO: dados estruturados ──────────────────────────────────────────────
   Sem isso a landing perderia o rich snippet já indexado pelo Google.
   O FAQPage é gerado a partir do DUVIDAS (data.ts) — o mesmo array que a seção
   de dúvidas mostra na tela, então o dado estruturado nunca diverge do texto. */
const SITE_URL = import.meta.env.VITE_SITE_URL || "https://blinkmed.com.br";

const homeJsonLd = {
  "@context": "https://schema.org",
  "@type": "MedicalOrganization",
  name: "Blink Telemedicina",
  url: SITE_URL,
  description:
    "Fale com um médico pelo celular em minutos, 24 horas, por vídeo ou mensagem. Receita, atestado e pedido de exame digitais quando o médico indicar. A partir de R$ 48,90.",
  logo: `${SITE_URL}/icons/icon-512.png?v=4`,
  sameAs: ["https://www.instagram.com/blinktelemedicina/"],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+55-54-99237-1000",
    contactType: "customer service",
    areaServed: "BR",
    availableLanguage: "Portuguese",
  },
  medicalSpecialty: "Clínica Médica",
  areaServed: { "@type": "Country", name: "Brasil" },
};

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: DUVIDAS.map((d) => ({
    "@type": "Question",
    name: d.q,
    acceptedAnswer: { "@type": "Answer", text: d.a },
  })),
};

/** Rola até uma seção. Se o chunk de baixo ainda não chegou (rede lenta),
 *  espera um pouco por ela em vez de não fazer nada. */
function rolarPara(id: string, tentativas = 24) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
    return;
  }
  if (tentativas > 0) window.setTimeout(() => rolarPara(id, tentativas - 1), 150);
}

const d = (ms: number) => ({ "--d": `${ms}ms` }) as CSSProperties;

/* ───────────────────────── Hero ───────────────────────── */

const SINTOMAS = ["dor de garganta", "febre", "infecção urinária", "enxaqueca", "alergia", "ansiedade"];

function TypingSintoma() {
  const [i, setI] = useState(0);
  const [txt, setTxt] = useState(SINTOMAS[0]);
  const [apagando, setApagando] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    const alvo = SINTOMAS[i];
    const pronto = txt === alvo;
    const vazio = txt === "";
    let t: number;
    if (!apagando && !pronto) t = window.setTimeout(() => setTxt(alvo.slice(0, txt.length + 1)), 60);
    else if (!apagando && pronto) t = window.setTimeout(() => setApagando(true), 1700);
    else if (apagando && !vazio) t = window.setTimeout(() => setTxt(txt.slice(0, -1)), 30);
    else {
      setApagando(false);
      setI((v) => (v + 1) % SINTOMAS.length);
      return;
    }
    return () => window.clearTimeout(t);
  }, [txt, apagando, i]);

  return (
    <span className="text-emerald-700 font-bold whitespace-nowrap">
      {txt}
      <span className="lt-caret" aria-hidden="true" />
    </span>
  );
}

type Fala = { de: "med" | "pac"; t: string } | { de: "rx" };
const FALAS: Fala[] = [
  { de: "med", t: "Olá! Sou o médico de plantão. Me conta o que você está sentindo." },
  { de: "pac", t: "Dor de garganta e febre desde ontem 😣" },
  { de: "med", t: "Entendi. A febre passou de 38°C? Está com dor para engolir?" },
  { de: "pac", t: "Chegou a 38,5 ontem à noite. Dói bastante pra engolir." },
  { de: "med", t: "Perfeito. Vou emitir sua receita agora." },
  { de: "rx" },
];
const PASSOS_TOTAL = FALAS.length + 2; // 2 "tempos" parados no fim antes de recomeçar

/** Card branco que flutua em volta do celular. */
function FloatCard({ icon, t, s, cor, className, delay }: { icon: IconName; t: string; s: string; cor: string; className: string; delay: number }) {
  return (
    <div className={`absolute z-20 ${className}`}>
      <div className="lt-parallax">
        <div className="lt-pop" style={d(delay)}>
          <div className="lt-bob lt-loop" style={d(delay * 2)}>
            <div className="flex items-center gap-2 sm:gap-2.5 rounded-2xl bg-white pl-2 pr-3 py-2 sm:pl-2.5 sm:pr-3.5 sm:py-2.5 shadow-[0_14px_34px_-14px_rgba(15,23,42,.38)] ring-1 ring-slate-900/[.06]">
              <span className={`w-[30px] h-[30px] sm:w-9 sm:h-9 shrink-0 rounded-xl grid place-items-center text-white ${cor}`}>
                <Ico n={icon} className="w-[18px] h-[18px]" />
              </span>
              <span className="leading-tight whitespace-nowrap">
                <span className="block text-[11.5px] sm:text-[12.5px] font-black text-slate-900">{t}</span>
                <span className="block text-[10.5px] sm:text-[11px] font-semibold text-slate-500">{s}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Celular escuro com a consulta em vídeo acontecendo — a "prova" visual. */
function HeroPhone() {
  const [ref, visivel] = useInView<HTMLDivElement>({ once: false, threshold: 0.12, rootMargin: "60px 0px" });
  const reduz = prefersReducedMotion();
  // Começa já com 2 falas na tela: o celular nunca aparece "vazio" no 1º quadro.
  const [passo, setPasso] = useState(reduz ? FALAS.length : 2);
  const [seg, setSeg] = useState(187);
  const tocando = visivel && !reduz;

  useEffect(() => {
    if (!tocando) return;
    const a = window.setInterval(() => setPasso((p) => (p + 1 > PASSOS_TOTAL ? 1 : p + 1)), 2300);
    const b = window.setInterval(() => setSeg((s) => (s >= 1799 ? 0 : s + 1)), 1000);
    return () => {
      window.clearInterval(a);
      window.clearInterval(b);
    };
  }, [tocando]);

  const n = Math.min(passo, FALAS.length);
  const visiveis = FALAS.slice(0, n).filter((f): f is Extract<Fala, { t: string }> => f.de !== "rx").slice(-3);
  const base = Math.max(0, FALAS.slice(0, n).filter((f) => f.de !== "rx").length - 3);
  const receita = n >= FALAS.length;
  const digitando = !reduz && passo < FALAS.length && FALAS[passo]?.de === "med";
  const mm = String(Math.floor(seg / 60)).padStart(2, "0");
  const ss = String(seg % 60).padStart(2, "0");

  return (
    <div ref={ref} className={`relative mx-auto w-[244px] min-[380px]:w-[258px] sm:w-[284px] ${visivel ? "" : "lt-off"}`}>
      <div className="lt-aura lt-loop" style={{ inset: "-12% -30%", "--c": "rgba(16,185,129,.30)" } as CSSProperties} aria-hidden="true" />
      {/* cartões + aparelho num bloco próprio: os cartões se posicionam pelo APARELHO, não pelo rótulo de baixo */}
      <div className="relative">

      {/* cartões flutuando — no celular só 2, encostados nos cantos do aparelho */}
      <FloatCard icon="receita" t="Receita digital" s="ICP-Brasil" cor="bg-gradient-to-br from-emerald-400 to-emerald-600" delay={500} className="-left-3 -top-5 sm:top-[-3%] sm:-left-[150px] lg:-left-[104px] xl:-left-[150px]" />
      <FloatCard icon="shield" t="CRM conferido" s="no CFM" cor="bg-gradient-to-br from-sky-400 to-blue-600" delay={700} className="-right-3 -bottom-6 sm:bottom-auto sm:top-[27%] sm:-right-[140px] lg:-right-[70px] xl:-right-[140px]" />
      <FloatCard icon="atestado" t="Atestado" s="validade legal" cor="bg-gradient-to-br from-amber-400 to-orange-500" delay={900} className="hidden sm:block lg:hidden xl:block sm:bottom-[33%] sm:-left-[162px] lg:-left-[112px] xl:-left-[162px]" />
      <FloatCard icon="browser" t="Sem aplicativo" s="direto no navegador" cor="bg-gradient-to-br from-violet-400 to-indigo-600" delay={1100} className="hidden sm:block lg:hidden xl:block sm:bottom-[5%] sm:-right-[150px] lg:-right-[70px] xl:-right-[150px]" />

      <div className="lt-enter relative rounded-[2.4rem] bg-slate-950 p-[9px] lt-phone-shadow" style={d(120)} aria-hidden="true">
        <div className="absolute top-[15px] left-1/2 -translate-x-1/2 w-[70px] h-[19px] rounded-full bg-black z-20" />
        <div className="relative rounded-[1.95rem] overflow-hidden bg-gradient-to-b from-[#154a40] to-[#17293f] h-[472px] min-[380px]:h-[492px] sm:h-[540px] flex flex-col">
          {/* topo da chamada */}
          <div className="pt-10 px-4 flex items-center justify-between text-[11px] text-white/80">
            <span className="inline-flex items-center gap-1.5 font-semibold">
              <span className="w-2 h-2 rounded-full bg-rose-500 lt-blink lt-loop" /> Consulta por mensagem
            </span>
            <span className="font-mono tabular-nums">{mm}:{ss}</span>
          </div>

          {/* "câmera" do médico — avatar genérico, sem foto */}
          {/* Área do vídeo REDUZIDA (15/10/2026, pedido do dono: "deixe menor
              aquela parte ali da consulta de vídeo... deve ficar mais aparente a
              parte de mensagem"). Encolher aqui é o que dá espaço ao chat logo
              abaixo, que é o foco agora. */}
          <div className="relative mx-3 mt-3 rounded-2xl h-[116px] min-[380px]:h-[124px] sm:h-[138px] bg-gradient-to-br from-emerald-700/75 via-teal-700/55 to-slate-700 overflow-hidden grid place-items-center">
            <div className="absolute inset-0 lt-dots-light opacity-70" />
            <div className="relative w-[62px] h-[62px]">
              <span className="absolute inset-0 rounded-full border-2 border-emerald-300/50 lt-ring lt-loop" />
              <span className="absolute inset-0 rounded-full border-2 border-emerald-300/40 lt-ring lt-loop" style={{ animationDelay: ".8s" }} />
              <span className="absolute inset-0 rounded-full border-2 border-emerald-300/30 lt-ring lt-loop" style={{ animationDelay: "1.6s" }} />
              <div className="lt-breathe lt-loop relative w-full h-full rounded-full bg-gradient-to-br from-emerald-300 to-teal-600 grid place-items-center overflow-hidden shadow-2xl">
                <svg viewBox="0 0 64 64" className="w-[74%] h-[74%] mt-3 text-white/95" aria-hidden="true">
                  <circle cx="32" cy="22" r="11" fill="currentColor" />
                  <path d="M10 60c1.5-12.5 10.5-20 22-20s20.5 7.5 22 20" fill="currentColor" />
                  <path d="M25 41v7a7 7 0 0 0 14 0v-7" fill="none" stroke="#0f766e" strokeWidth="2.4" strokeLinecap="round" />
                  <circle cx="39" cy="50" r="2.4" fill="#0f766e" />
                </svg>
              </div>
            </div>
            <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 rounded-full bg-white/90 text-emerald-800 text-[9.5px] font-black px-2 py-0.5">
              <Ico n="check" className="w-3 h-3" strokeWidth={3} /> CRM conferido
            </span>
            <div className="absolute bottom-2.5 left-2.5 flex items-center gap-2 bg-black/30 rounded-full pl-2.5 pr-3 py-1">
              <span className="lt-eq flex items-center gap-[3px] h-4">
                <span className="lt-loop" /><span className="lt-loop" /><span className="lt-loop" /><span className="lt-loop" /><span className="lt-loop" />
              </span>
              <span className="text-[10px] font-bold text-white">Médico de plantão</span>
            </div>
            {/* janelinha "você" */}
            <div className="absolute top-2 right-2 w-11 h-[56px] rounded-lg bg-gradient-to-br from-slate-400 to-slate-600 border border-white/25 overflow-hidden grid place-items-end justify-center pb-0.5">
              <svg viewBox="0 0 64 64" className="absolute top-2 left-1/2 -translate-x-1/2 w-8 h-8 text-slate-200" aria-hidden="true">
                <circle cx="32" cy="22" r="11" fill="currentColor" />
                <path d="M10 60c1.5-12.5 10.5-20 22-20s20.5 7.5 22 20" fill="currentColor" />
              </svg>
              <span className="relative text-[9px] font-bold text-white/85">Você</span>
            </div>
          </div>

          {/* chat */}
          <div className="flex-1 px-3 pt-3 flex flex-col justify-end gap-2 overflow-hidden">
            {visiveis.map((f, k) => (
                <div
                  key={base + k}
                  className={`lt-msg max-w-[86%] text-[11.5px] leading-snug px-3 py-2 rounded-2xl ${
                    f.de === "pac" ? "self-end bg-emerald-500 text-emerald-950 rounded-br-md font-semibold" : "self-start bg-white/[.17] text-white rounded-bl-md"
                  }`}
                >
                  {f.t}
                </div>
            ))}
            {digitando && (
              <div className="lt-msg self-start bg-white/[.17] rounded-2xl rounded-bl-md px-3 py-2.5 flex gap-1">
                <span className="lt-dot lt-loop w-1.5 h-1.5 rounded-full bg-white/70" />
                <span className="lt-dot lt-loop w-1.5 h-1.5 rounded-full bg-white/70" />
                <span className="lt-dot lt-loop w-1.5 h-1.5 rounded-full bg-white/70" />
              </div>
            )}
            {receita && (
              <div className="lt-rx flex items-center gap-2.5 rounded-2xl bg-white px-3 py-2.5 shadow-lg">
                <span className="w-8 h-8 shrink-0 rounded-xl bg-emerald-600 text-white grid place-items-center">
                  <Ico n="receita" className="w-[18px] h-[18px]" />
                </span>
                <span className="min-w-0 leading-tight">
                  <span className="block text-[11.5px] font-black text-slate-900">Receita digital emitida</span>
                  <span className="block text-[10px] font-semibold text-emerald-700">Assinada · ICP-Brasil</span>
                </span>
                <span className="ml-auto w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
                  <Ico n="check" className="w-3.5 h-3.5" strokeWidth={3} />
                </span>
              </div>
            )}
          </div>

          {/* controles */}
          <div className="p-4 flex items-center justify-center gap-2.5">
            <span className="w-10 h-10 rounded-full bg-white/20 grid place-items-center text-white"><Ico n="mic" className="w-[18px] h-[18px]" /></span>
            <span className="w-10 h-10 rounded-full bg-white/20 grid place-items-center text-white"><Ico n="video" className="w-[18px] h-[18px]" /></span>
            <span className="w-10 h-10 rounded-full bg-white/20 grid place-items-center text-white"><Ico n="chat" className="w-[18px] h-[18px]" /></span>
            <span className="w-10 h-10 rounded-full bg-rose-500 grid place-items-center text-white"><HangUp className="w-5 h-5" /></span>
          </div>
        </div>
      </div>
      </div>
      <p className="mt-10 sm:mt-4 text-center text-[10px] font-bold uppercase tracking-[.18em] text-slate-400">Simulação ilustrativa</p>
    </div>
  );
}

const REFORCOS = ["Sem aplicativo", "Receita digital ICP-Brasil", "Atestado médico", "Pedido de exames", "CRM conferido no CFM", "Sigilo médico e LGPD", "Chat ou videochamada", "Atende todo o Brasil"];

function Faixa() {
  const [ref, visivel] = useInView<HTMLDivElement>({ once: false, threshold: 0, rootMargin: "40px 0px" });
  return (
    <div ref={ref} className={`lt-marquee-mask border-y border-slate-100 bg-slate-50/80 py-3.5 overflow-hidden ${visivel ? "" : "lt-off"}`}>
      <div className="lt-marquee lt-loop text-[13px] font-bold text-slate-500">
        {[0, 1].map((dup) => (
          <div key={dup} className="flex items-center gap-9 pr-9" aria-hidden={dup === 1}>
            {REFORCOS.map((t) => (
              <span key={t} className="inline-flex items-center gap-2 whitespace-nowrap">
                <span className="w-4 h-4 rounded-full bg-emerald-500 text-white grid place-items-center">
                  <Ico n="check" className="w-2.5 h-2.5" strokeWidth={3.5} />
                </span>
                {t}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────────────────────── Página ───────────────────────── */

export default function HomePage() {
  const navigate = useNavigate();
  const [heroRef, heroVisivel] = useInView<HTMLElement>({ once: false, threshold: 0, rootMargin: "0px" });

  // Portado da home anterior — sem isto a landing quebraria dois comportamentos:
  //  1. quem JÁ está logado (paciente ou médico/admin) veria a landing em vez do
  //     portal/painel — era bug real corrigido antes nesta mesma home;
  //  2. o funil de Marketing perderia a etapa "Visitas", que conta `page_view`
  //     com label "home" (MarketingPanel lê exatamente esse evento).
  useEffect(() => {
    const destino = destinoLogado();
    if (destino) {
      navigate(destino, { replace: true });
      return;
    }
    trackEvent("page_view", "home", { page: "home", session_id: getSessionId() });
  }, [navigate]);

  // Baixa as seções de baixo logo depois da primeira pintura (não compete com o hero).
  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
    const go = () => void importarSecoes().catch(() => {});
    if (w.requestIdleCallback) w.requestIdleCallback(go, { timeout: 1200 });
    else window.setTimeout(go, 300);
  }, []);

  const ir = (destino: string) => () => navigate(destino);

  return (
    <>
      <SeoHead
        title="Blink Telemedicina | Médico online 24h em minutos"
        description="Fale com um médico pelo celular em minutos, 24 horas, por vídeo ou mensagem. Receita, atestado e pedido de exame digitais quando o médico indicar. A partir de R$ 48,90."
        keywords="consulta médica online, telemedicina, receita digital, atestado médico, consulta online, médico online, receita médica online, teleconsulta, atestado digital, laudo médico online, atestado médico"
        canonicalPath="/"
        jsonLd={[homeJsonLd, faqJsonLd]}
      />

      {/* padding inferior = altura da barra fixa do atendente (+ área segura do iPhone) */}
      <div className="lt-root min-h-screen bg-white text-slate-900 antialiased">
        {/* ── topo ── fundo sólido (sem backdrop-blur: no celular ele recalcula a cada quadro de rolagem) */}
        <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/[.97]">
          <div className="max-w-6xl mx-auto px-4 sm:px-5 h-16 flex items-center gap-2 sm:gap-3">
            <button onClick={ir("/")} className="flex items-center gap-2 sm:gap-2.5 cursor-pointer min-h-[44px] shrink-0" aria-label="Blink Telemedicina — início">
              <span className="w-8 h-8 sm:w-9 sm:h-9 shrink-0 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center text-white shadow-lg shadow-emerald-600/20">
                <Bolt className="w-[18px] h-[18px]" />
              </span>
              {/* Duas linhas compactas: numa linha só, "Blink Telemedicina" empurrava o CTA para fora no celular. */}
              <span className="flex flex-col leading-none text-left">
                <span className="font-black text-[17px] min-[400px]:text-lg leading-none tracking-tight">Blink</span>
                <span className="mt-[2px] text-[8px] min-[400px]:text-[8.5px] sm:text-[10px] font-bold uppercase tracking-[0.08em] min-[400px]:tracking-[0.12em] sm:tracking-[0.18em] text-emerald-700/80">Telemedicina</span>
              </span>
            </button>
            <nav className="ml-auto hidden lg:flex items-center gap-7 text-sm font-semibold text-slate-600">
              <button onClick={() => rolarPara("como-funciona")} className="hover:text-emerald-700 transition-colors cursor-pointer">Como funciona</button>
              <button onClick={() => rolarPara("documentos")} className="hover:text-emerald-700 transition-colors cursor-pointer">O que você recebe</button>
              <button onClick={() => rolarPara("duvidas")} className="hover:text-emerald-700 transition-colors cursor-pointer">Dúvidas</button>
            </nav>
            {/* Ações do topo. "Sou médico" → /medico (login da área médica): no celular só o
                estetoscópio, discreto (não compete com Entrar/Cadastrar); a partir de `sm` ganha o texto. */}
            <div className="ml-auto lg:ml-0 flex items-center gap-1 sm:gap-2.5">
              <button onClick={ir("/medico")} aria-label="Sou médico — entrar na área médica" className="inline-flex items-center justify-center gap-1 sm:gap-1.5 h-8 sm:h-11 px-1 min-[400px]:px-1.5 sm:px-3 rounded-full text-slate-400 hover:text-emerald-700 text-[11px] sm:text-sm font-semibold transition-colors active:scale-[.97] cursor-pointer whitespace-nowrap">
                <Ico n="stethoscope" className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px]" />
                <span className="hidden min-[380px]:inline">Médico</span>
              </button>
              <button onClick={ir("/paciente")} aria-label="Entrar" className="inline-flex items-center justify-center px-2 min-[400px]:px-2.5 sm:px-5 h-9 sm:h-11 rounded-full border border-slate-200 text-slate-700 hover:border-emerald-600 hover:text-emerald-700 text-[13px] sm:text-sm font-bold transition-colors active:scale-[.97] cursor-pointer whitespace-nowrap">
                Entrar
              </button>
              <button onClick={ir("/paciente?modo=cadastro")} className="inline-flex items-center bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] sm:text-sm font-bold px-2 min-[400px]:px-2.5 sm:px-5 h-9 sm:h-11 rounded-full shadow-lg shadow-emerald-600/20 transition-all active:scale-[.97] cursor-pointer whitespace-nowrap">
                Cadastrar
              </button>
            </div>
          </div>
        </header>

        {/* ── HERO ── */}
        <section ref={heroRef} className={`relative overflow-hidden ${heroVisivel ? "" : "lt-off"}`}>
          <div className="lt-hero-bg absolute inset-0" aria-hidden="true" />
          <div className="lt-aura lt-loop" style={{ top: "-18%", left: "-20%", width: "70vw", height: "70vw", maxWidth: 720, maxHeight: 720, "--c": "rgba(16,185,129,.16)" } as CSSProperties} aria-hidden="true" />
          <div className="lt-aura lt-loop" style={{ top: "6%", right: "-24%", width: "60vw", height: "60vw", maxWidth: 640, maxHeight: 640, animationDelay: "-5s", "--c": "rgba(14,165,233,.12)" } as CSSProperties} aria-hidden="true" />

          <div className="relative max-w-6xl mx-auto px-4 sm:px-5 pt-9 pb-12 sm:pt-14 md:pt-20 md:pb-20 grid lg:grid-cols-[1.06fr_.94fr] gap-14 lg:gap-6 items-center">
            <div className="text-center lg:text-left">
              <span className="lt-enter inline-flex items-center gap-2 text-[12px] md:text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200/80 px-3.5 py-1.5 rounded-full">
                <span className="relative inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 lt-ping lt-loop text-emerald-500" />
                Médicos online agora
              </span>

              <h1 className="lt-enter-h mt-5 text-[2.05rem] min-[380px]:text-[2.25rem] sm:text-5xl lg:text-[3.55rem] font-black tracking-tight leading-[1.04]">
                Consulta médica
                <br />
                <span className="lt-grad">sem sair de casa.</span>
              </h1>

              <p className="lt-enter mt-5 text-[16.5px] md:text-xl text-slate-600 leading-relaxed max-w-xl mx-auto lg:mx-0" style={d(90)}>
                Com <TypingSintoma /> ou qualquer outro sintoma: você fala com um médico de verdade — por{" "}
                <strong className="text-slate-900 font-bold">vídeo ou mensagem</strong> — e sai da consulta com{" "}
                <strong className="text-slate-900 font-bold">receita, atestado ou pedido de exames</strong>.
              </p>

              <div className="lt-enter mt-7 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 justify-center lg:justify-start" style={d(170)}>
                {/* ESTE botão (o CTA principal do hero) vai DIRETO ao cadastro —
                    pedido do dono 15/10/2026. É o clique de quem acabou de ler a
                    oferta e quer começar, então não faz sentido passar pelo login
                    antes. O "Voltar" não confunde mais: a 1ª etapa do cadastro
                    agora tem a caixa "Já tem conta na Blink? → Entrar na minha
                    conta" no topo.
                    Os DEMAIS CTAs (botão do cabeçalho e os das seções) continuam
                    indo ao LOGIN, onde o cadastro é o primeiro bloco da tela.
                    Quem já tem sessão cai no portal nos dois casos. */}
                <button
                  onClick={ir("/paciente?modo=cadastro")}
                  className="lt-sheen lt-loop group whitespace-nowrap inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[17px] md:text-lg font-bold px-7 min-h-[58px] rounded-2xl shadow-xl shadow-emerald-600/25 transition-colors active:scale-[.98] cursor-pointer"
                >
                  Consultar por R$ {PRECO}
                  <Ico n="arrowRight" className="w-5 h-5 transition-transform group-hover:translate-x-0.5" />
                </button>
                <button onClick={() => rolarPara("como-funciona")} className="inline-flex items-center justify-center gap-1.5 text-[15px] font-bold text-slate-600 hover:text-emerald-700 px-3 min-h-[48px] transition-colors cursor-pointer">
                  Ver como funciona <Ico n="arrowDown" className="w-4 h-4" />
                </button>
              </div>
              {/* uma linha só, nunca quebra */}
              <p className="lt-enter mt-2 text-[13px] min-[380px]:text-[14px] text-slate-500 whitespace-nowrap" style={d(220)}>
                Pagamento único, sem mensalidade
              </p>

              {/* Selos: no celular é grade de 3 colunas (ícone em cima, texto curto embaixo) —
                  em linha corrida o 3º item caía sozinho numa linha (print do dono, 390px). */}
              <ul className="lt-enter mt-7 grid grid-cols-3 gap-2 sm:flex sm:flex-nowrap sm:items-center sm:justify-center lg:justify-start sm:gap-x-5 sm:gap-y-2" style={d(280)}>
                {[
                  { i: "shield" as const, c: "CRM no CFM", l: "CRM conferido no CFM" },
                  { i: "lock" as const, c: "Sigilo e LGPD", l: "Sigilo e LGPD" },
                  { i: "receita" as const, c: "Válida no Brasil", l: "Receita válida no Brasil" },
                ].map((x) => (
                  <li key={x.l} className="flex flex-col sm:flex-row items-center gap-1 sm:gap-1.5 rounded-2xl sm:rounded-none bg-white/80 sm:bg-transparent border border-slate-200/80 sm:border-0 px-0.5 py-2.5 sm:p-0 text-center">
                    <span className="w-7 h-7 sm:w-auto sm:h-auto rounded-full bg-emerald-50 sm:bg-transparent grid place-items-center text-emerald-600">
                      <Ico n={x.i} className="w-4 h-4" strokeWidth={2.3} />
                    </span>
                    <span className="sm:hidden text-[11.5px] min-[380px]:text-[12px] leading-tight font-bold text-slate-700 whitespace-nowrap">{x.c}</span>
                    <span className="hidden sm:inline text-[13.5px] font-semibold text-slate-600 whitespace-nowrap">{x.l}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="pt-4 lg:pt-0">
              <HeroPhone />
            </div>
          </div>
        </section>

        <Faixa />

        <Suspense fallback={<div className="min-h-[120vh]" aria-hidden="true" />}>
          <Secoes />
        </Suspense>

        {/* Barra fixa ancorada embaixo (antes era uma bolinha flutuante no canto). */}
        <FloatingWhatsApp />
      </div>
    </>
  );
}
